import constants from "../../../../../../appConstants";
import { Button } from "@material-ui/core";
import { getSpecialNeeds, SpecialNeedsRenderer , StopTypeRenderer } from '@dataGrid/components/cellRenderer';
import {
  mergeUserConfig,
  svcValueGetter,
  hasDefaultDeliveryWindow,
} from '@dataGrid/gridUtilities';
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from '@dataGrid/components/columnFilters';
import {
  getDeliveryStatusFromShipmentDelCode,
  formatNumber,
  updateGridColumnHeaderNames,
  getPickupStopStatus,
  formatDeliveryOrServiceWindow,
  normalizeDeliveryWindow,
  getStatusDisplayValues,
} from "../../../utils/utils";
import { unassignPickup } from "../../utils/pickupActions";
import { gridShortHeaderNamesConfig } from "./constants";
import PickupNumberRenderer from "./PickupNumberRenderer";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }

  static getConfiguration({
    userConfig,
    featureFlags: { enableColumnShortForm },
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration();
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig,
    });
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm,
    });
  }

  static isPickupStop(data) {
    return data.stopType === constants.STOP_TYPE.Pickup;
  }

  static isStopCompleted(data) {
    return data.isStopCompleted;
  }


  static StatusValueGetter(params) {
    const { data } = params;
    const dispatchStore = params.context?.DispatchStore;
    const enableReturnExceptionReasonLabels = Boolean(
      params.context?.enableReturnExceptionReasonLabels
    );
    if (GridConfig.isStopCompleted(data)) {
      if(GridConfig.isPickupStop(data)){
        return getPickupStopStatus(data)
      }
      const deliveryStatus = getDeliveryStatusFromShipmentDelCode(
        data.deliveryCode
      );
      switch (deliveryStatus) {
        case constants.DELIVERY_STATUS.RETURN: {
          const returnCodeOrLabel = enableReturnExceptionReasonLabels
            ? dispatchStore?.getReturnDetails(data.returnCode)?.description ?? data.returnCode
            : data.returnCode;
          return `${deliveryStatus} (${returnCodeOrLabel})`;
        }
        case constants.DELIVERY_STATUS.DELIVERY_EXCEPTION: {
          const exceptionValues = enableReturnExceptionReasonLabels
            ? getStatusDisplayValues(data.exceptionCode ?? [], dispatchStore)
            : data.exceptionCode ?? [];
          return `${deliveryStatus} (${exceptionValues.join(', ')})`
        }
        default:
          return deliveryStatus;
      }
    }
    return constants.DELIVERY_STATUS.PENDING;
  }

  static isReturnDelivery({ data }) {
    return (
      getDeliveryStatusFromShipmentDelCode(data.deliveryCode) ===
      constants.DELIVERY_STATUS.RETURN
    );
  }

  static formatDeliveryWindow(params) {    
    const filteredDeliveryWindow = normalizeDeliveryWindow(params.data.deliveryWindow)
      .filter(window => {
        const isDefaultWindow = hasDefaultDeliveryWindow(window);
        return !isDefaultWindow || params.data.isApt;
      })
    return formatDeliveryOrServiceWindow(filteredDeliveryWindow);
  }

  static getReadyClosetime = (params) => {
    const { readyTime, closeTime } = params.data;
    if (readyTime && closeTime) {
      return `${readyTime} - ${closeTime}`;
    }
    return "";
  };

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getCommaSeparatedProNumber(params) {
    if (params.data.stopType === constants.STOP_TYPE.Pickup) {
      return params.data.shipments
        .filter((pro) => pro.proId && pro.proId !== '0')
        .map((pro) => pro.proId)
        .join(', ');
    }
    return params.data.proId;
  }

  static getCommaSeparatedPickupNumber(params) {
    const pickupNumbers = (params?.data?.pickupNumberIdList ?? []).map((pickup) => pickup.pickupNumber);
    return [...new Set(pickupNumbers)].join(", ");
  }

  static UnassignButtonRenderer(props) {
    return (
      <div>
        {props.data.stopType === constants.STOP_TYPE.Pickup &&
        !props.data.isStopCompleted ? (
          <Button
            className={"dispatch-grid-active-button"}
            onClick={() =>
              unassignPickup({
                pickups: [
                {
                  pickupId: props.data.pickupId,
                  groupId: props.data.groupId
                }
                ],
                driverId: props.data.driver.driverId,
                terminalCode: props.context.terminalCode,
                glEventHub: props.context.glEventHub,
                DispatchStore: props.context.DispatchStore,
              })
            }
            data-automationid="Unassign"
          >
            Unassign
          </Button>
        ) : (
          ""
        )}
      </div>
    );
  }

  static getStopType = (params) => {
    return params.data.stopType === constants.STOP_TYPE.Pickup ? 'P' : 'D';
  }

  static getETA = (params) => {
    return params.data.actualArrivalTime ||  params.data.plannedArrivalTime || ''
  }

  static getDefaultConfiguration() {
    return {
      title: constants.gridNames.DriverInfoGrid,
      keyColumn: "driverInfoPopup",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      rowClassRules: {
        violationRowBGColor: GridConfig.isReturnDelivery,
      },
      columns: [
        {
          headerName: "Action",
          headerTooltip: "Assign/UnAssign Pickup",
          minWidth: constants.columnWidth.ASSIGN_ACTION,
          cellClass: "gridCell-leftAlign",
          colId: "actionCol",
          cellRenderer: GridConfig.UnassignButtonRenderer,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          filter: false,
          sortable: false,
        },
        {
          headerName: "Seq",
          field: "stopSequence",
          headerTooltip: "Sequence",
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "P/D",
          headerTooltip: "Pickup or Delivery",
          minWidth: constants.columnWidth.W_160,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          valueGetter : GridConfig.getStopType,
          cellRenderer: StopTypeRenderer,
          colId : "stopType"
        },
        {
          headerName: "Pro #",
          headerTooltip: "Pro Number",
          field: "proId",
          minWidth: constants.columnWidth.PRO_NUMBER,
          cellClass: (params) => {
            return params.data.stopType === constants.STOP_TYPE.Delivery
              ? ['gridCell-leftAlign', 'actionable-cell']
              : ['gridCell-leftAlign'];
          },
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
          valueGetter: GridConfig.getCommaSeparatedProNumber,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Pickup #",
          headerTooltip: "Pickup Number",
          field: "pickupNumber",
          cellClass: ['gridCell-leftAlign', 'actionable-cell'],
          cellRenderer: PickupNumberRenderer,
          minWidth: constants.columnWidth.W_300,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Status",
          headerTooltip: "Status",
          minWidth: constants.columnWidth.STATUS,
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.StatusValueGetter,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Spl Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: getSpecialNeeds,
          headerTooltip: "Special Needs",
          cellClass: "gridCell-leftAlign",
          cellRenderer: SpecialNeedsRenderer,
          cellRendererParams: {
            getAPTWindow: GridConfig.formatDeliveryWindow,
          },
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
        },
        {
          headerName: "Customer",
          headerTooltip: "Customer Name",
          field: "displayCustomerName",
          minWidth: constants.columnWidth.CUSTOMER_NAME,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Address",
          headerTooltip: "Address",
          field: "address",
          minWidth: constants.columnWidth.DRIVER_ADDRESS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "City",
          headerTooltip: "City",
          field: "city",
          minWidth: constants.columnWidth.CITY,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "State",
          headerTooltip: "State",
          field: "state",
          minWidth: constants.columnWidth.STATE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Zip",
          headerTooltip: "Zip",
          field: "zip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Weight",
          headerTooltip: "Weight",
          field: "weight",
          minWidth: constants.columnWidth.W_90,
          cellClass: "gridCell-leftAlign",
          valueFormatter : (params) => formatNumber(params.data.weight),
          filter: MultiSelectFilter,
        },
        {
          headerName: "Cube",
          headerTooltip: "Cube",
          field: "cube",
          minWidth: constants.columnWidth.W_85,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Pcs",
          headerTooltip: "Pieces",
          field: "pieces",
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "ETA",
          headerTooltip: "ETA",
          valueGetter: GridConfig.getETA,
          minWidth: constants.columnWidth.ARRIVAL_TIME,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "ETD",
          headerTooltip: "ETD",
          field: "departureTime",
          minWidth: constants.columnWidth.EXPECTED_DEPARTURE_TIME,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Ready Close Time",
          headerTooltip: "Ready Close Time",
          minWidth: constants.columnWidth.DRIVER_INFO,
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.getReadyClosetime,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Apt/Del Window",
          headerTooltip: "Appointment/Delivery Window",
          minWidth: constants.columnWidth.DRIVER_INFO,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
          valueGetter: GridConfig.formatDeliveryWindow,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "SVC",
          valueGetter: svcValueGetter,
          minWidth: constants.columnWidth.W_120,
          headerTooltip: "Service Level",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Ldg Zone",
          headerTooltip: "Loading Zone",
          field: "loadingZone",
          minWidth: constants.columnWidth.LDG_ZONE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
      ],
    };
  }
}
